import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setCurrentEditingCR
} from '../../../store/slices/reviewCRSlice';
import { fetchConstructionReviews } from '../../../store/slices/databaseSlice';
import {
  selectSearchQuery,
  selectSortBy,
  selectSortOrder,
  selectPaginatedCRs
} from '../../../store/selectors/reviewCRSelectors';
import {
  selectIsLoadingDbCRs,
  selectDbCRsPagination
} from '../../../store/selectors/databaseSelectors';
import { ConstructionReview } from '../../../services/construction-reviews.service';
import { ConfirmationModal, ConfirmationType } from '../../../components/shared/ui';
import { UI_CONFIG, ROUTES } from '../../../constants';

interface CRListProps {
  onSelectCR: (cr: ConstructionReview) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export const CRList: React.FC<CRListProps> = ({ onSelectCR, currentPage, onPageChange }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [manualEditsWarningCR, setManualEditsWarningCR] = useState<ConstructionReview | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Redux selectors
  const searchQuery = useAppSelector(selectSearchQuery);
  const sortBy = useAppSelector(selectSortBy);
  const sortOrder = useAppSelector(selectSortOrder);
  const crs = useAppSelector(selectPaginatedCRs);
  const isLoadingCRs = useAppSelector(selectIsLoadingDbCRs);
  const pagination = useAppSelector(selectDbCRsPagination);

  const { maxVisiblePages } = UI_CONFIG.pagination;

  // Fetch from server whenever page, sort, or search changes
  const fetchPage = useCallback((page: number, search: string, sort: string, order: string) => {
    dispatch(fetchConstructionReviews({
      page,
      limit: UI_CONFIG.pagination.itemsPerPage,
      search: search || undefined,
      sortBy: sort as 'created_at' | 'updated_at' | 'task_name',
      sortOrder: order as 'asc' | 'desc',
    }));
  }, [dispatch]);

  // Initial fetch
  useEffect(() => {
    fetchPage(currentPage, searchQuery, sortBy, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when page or sort changes (not search — that's debounced separately)
  useEffect(() => {
    fetchPage(currentPage, searchQuery, sortBy, sortOrder);
  }, [currentPage, sortBy, sortOrder, fetchPage]);

  // Debounce search — reset to page 1, then fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onPageChange(1);
      fetchPage(1, searchQuery, sortBy, sortOrder);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSort = (field: 'created_at' | 'updated_at' | 'task_name') => {
    if (sortBy === field) {
      dispatch(setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      dispatch(setSortBy(field));
      dispatch(setSortOrder('desc'));
    }
    onPageChange(1);
  };

  const handleSelectCR = (cr: ConstructionReview) => {
    dispatch(setCurrentEditingCR(cr));
    onSelectCR(cr);
  };

  const handleResumeInWorkflow = (cr: ConstructionReview) => {
    const hasManualEdits = Object.keys(cr.changes).length > 0;
    if (hasManualEdits) {
      setManualEditsWarningCR(cr);
      return;
    }
    navigate(`/create/${cr.id}`);
  };

  const handleConfirmResumeWithEdits = () => {
    if (manualEditsWarningCR) {
      navigate(`/create/${manualEditsWarningCR.id}`);
    }
    setManualEditsWarningCR(null);
  };

  const getSortIcon = (field: typeof sortBy) => {
    if (sortBy !== field) {
      return (
        <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortOrder === 'asc' ? (
      <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (isLoadingCRs && crs.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full min-h-[40vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-slate-300 border-t-blue-500 mb-3"></div>
        <p className="text-slate-500 text-sm font-medium">Loading Construction Reviews...</p>
      </div>
    );
  }

  const { total, totalPages } = pagination;
  const showFrom = total === 0 ? 0 : (currentPage - 1) * UI_CONFIG.pagination.itemsPerPage + 1;
  const showTo = Math.min(currentPage * UI_CONFIG.pagination.itemsPerPage, total);

  return (
    <div className="space-y-3">
      {/* Search and Filter Controls */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by job name or task ID"
            value={searchQuery}
            onChange={(e) => dispatch(setSearchQuery(e.target.value))}
            className="w-full pl-12 pr-10 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-400/20 focus:border-orange-400 bg-slate-50/30 hover:bg-white transition-all duration-200 placeholder-slate-400"
          />
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center">
              <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          {searchQuery && (
            <button
              onClick={() => dispatch(setSearchQuery(''))}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors duration-200"
            >
              <svg className="w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="text-xs text-slate-600 font-medium bg-gradient-to-r from-sky-50/70 to-slate-100/80 rounded-xl px-3 py-1.5 border border-sky-200/60 backdrop-blur-sm shadow-sm">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Showing {showFrom}-{showTo} of {total} Construction Reviews
        </div>
      </div>

      {/* CR List */}
      {crs.length === 0 ? (
        <div className="bg-gradient-to-br from-white via-slate-50/30 to-orange-50/20 rounded-2xl border border-white/60 shadow-xl backdrop-blur-sm p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">No Construction Reviews Found</h3>
          <p className="text-slate-600 text-sm leading-relaxed max-w-sm mx-auto">
            {searchQuery ? 'Try adjusting your search criteria to find matching construction reviews.' : 'No Construction Reviews have been created yet. Create your first one to get started.'}
          </p>
        </div>
      ) : (
        <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-white/80 shadow-xl overflow-hidden">
          {isLoadingCRs && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-2xl">
              <div className="animate-spin rounded-full h-7 w-7 border-[3px] border-slate-200 border-t-blue-500"></div>
            </div>
          )}
          <table className="w-full divide-y divide-slate-100 table-fixed">
            <thead className="bg-gradient-to-r from-orange-50 to-slate-50">
              <tr>
                <th
                  className="w-[30%] px-3 py-1.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200/60 transition-colors duration-200"
                  onClick={() => handleSort('task_name')}
                >
                  <div className="flex items-center gap-1">
                    <span>Task</span>
                    {getSortIcon('task_name')}
                  </div>
                </th>
                <th className="w-[6%] px-3 py-1.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Fields
                </th>
                <th className="w-[6%] px-3 py-1.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Edited
                </th>
                <th
                  className="w-[18%] px-3 py-1.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200/60 transition-colors duration-200"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Created</span>
                    {getSortIcon('created_at')}
                  </div>
                </th>
                <th
                  className="w-[18%] px-3 py-1.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200/60 transition-colors duration-200"
                  onClick={() => handleSort('updated_at')}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>Updated</span>
                    {getSortIcon('updated_at')}
                  </div>
                </th>
                <th className="w-[14%] px-3 py-1.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/60 backdrop-blur-sm divide-y divide-slate-100">
              {crs.map((cr) => (
                <tr key={cr.id} className="hover:bg-slate-50/80 transition-all duration-200 h-10">
                  <td className="px-3 py-1">
                    <div className="truncate" title={`${cr.task_name} (${cr.task_id})`}>
                      <span className="text-xs font-semibold text-slate-900">{cr.task_name}</span>
                      <span className="text-[10px] text-slate-400 font-mono ml-1.5">({cr.task_id})</span>
                    </div>
                  </td>
                  <td className="px-3 py-1 text-center">
                    <div className="text-xs font-medium text-slate-900">
                      {Object.keys(cr.updated_cr).length}
                    </div>
                  </td>
                  <td className="px-3 py-1 text-center">
                    <div className="text-xs font-medium text-slate-900">
                      {Object.keys(cr.changes).length > 0 ? (
                        <span className="text-orange-700 font-semibold">{Object.keys(cr.changes).length}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1 text-center">
                    <div className="flex items-center justify-center gap-1.5" title={new Date(cr.created_at).toLocaleString()}>
                      <span className="text-xs text-slate-600">{new Date(cr.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="text-[10px] text-slate-400">{new Date(cr.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                      {cr.created_by && (
                        <>
                          {cr.created_by.avatar_url && <img src={cr.created_by.avatar_url} alt="" className="w-4 h-4 rounded-full shrink-0" />}
                          <span className="text-[9px] text-slate-400">{cr.created_by.name}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1 text-center">
                    <div className="flex items-center justify-center gap-1.5" title={new Date(cr.updated_at).toLocaleString()}>
                      <span className="text-xs text-slate-600">{new Date(cr.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="text-[10px] text-slate-400">{new Date(cr.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                      {cr.updated_by && (
                        <>
                          {cr.updated_by.avatar_url && <img src={cr.updated_by.avatar_url} alt="" className="w-4 h-4 rounded-full shrink-0" />}
                          <span className="text-[9px] text-slate-400">{cr.updated_by.name}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => handleSelectCR(cr)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md shadow-sm hover:shadow-md transition-all duration-200"
                        title="Edit CR Output"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Output
                      </button>
                      <button
                        onClick={() => handleResumeInWorkflow(cr)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 rounded-md shadow-sm hover:shadow-md transition-all duration-200"
                        title="Edit in Workflow"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Workflow
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 shadow-xl px-4 py-2 flex items-center justify-between">
          <div className="flex items-center text-sm text-slate-600 font-medium">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707L16.414 6.414A1 1 0 0015.707 6H7a2 2 0 00-2 2v11a2 2 0 002 2z" />
              </svg>
              <span>Page {currentPage} of {totalPages}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white/60 hover:bg-white border border-slate-200/60 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/60 disabled:hover:text-slate-600"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Prev
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(maxVisiblePages, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= maxVisiblePages) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-2 py-1 text-xs font-medium rounded-lg transition-all duration-200 ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white shadow-lg border border-blue-500'
                        : 'text-slate-600 hover:text-slate-900 bg-white/60 hover:bg-white border border-slate-200/60 shadow-sm hover:shadow-md'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white/60 hover:bg-white border border-slate-200/60 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/60 disabled:hover:text-slate-600"
            >
              Next
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={manualEditsWarningCR !== null}
        onClose={() => setManualEditsWarningCR(null)}
        onConfirm={handleConfirmResumeWithEdits}
        title="CR Has Manual Edits"
        message={
          <div className="space-y-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.834-1.964-.834-2.732 0L4.732 16c-.77.833.192 3 1.732 3z" />
              </svg>
              {manualEditsWarningCR ? Object.keys(manualEditsWarningCR.changes).length : 0} manually edited field(s)
            </div>
            <p>Editing a workflow field that maps to a manually edited CR output field will overwrite that manual change.</p>
            <div className="px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-[11px] leading-relaxed">
              <span className="font-semibold">Example:</span> If you manually edited MODULES (S1) and then change the Module dropdown in the workflow, your manual edit on MODULES (S1) will be replaced with the new selection.
            </div>
          </div>
        }
        type={ConfirmationType.WARNING}
        confirmButtonText="Continue to Workflow"
        cancelButtonText="Cancel"
      />
    </div>
  );
};
